// Decoys tab of the Apps page
import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import DecoyAppModal from './DecoyAppModal';
import Card from './Card';
import PrimaryButton from './PrimaryButton';
import SearchInput from './SearchInput';
import StatusBadge from './StatusBadge';
import InfoCard from './InfoCard';
import EmptyState from './EmptyState';
import ErrorAlert from './ErrorAlert';
import { SkeletonTableRows, SkeletonListToolbar, SkeletonListCard } from './Skeleton';
import LoadingAnnouncer from './LoadingAnnouncer';
import DeleteModal from './DeleteModal';
import { HeaderCell, BodyCell } from './TableCell';
import { IconPlus, IconRefresh, IconEdit, IconTrash } from '../icons';
import { formatDate } from '../utils/format';

const COL = {
  name: 260,
  status: 132,
  proxy: 170,
  url: 260,
  checked: 190,
  actions: 124,
};

const STORE_TYPE_LABELS = {
  appstore: 'App Store',
  googleplay: 'Google Play',
  other: 'Other',
};

const ROW = 'flex items-stretch h-16 border-b border-line/60 last:border-b-0 hover:bg-surface transition-colors';

const SKELETON_COLUMNS = [
  { width: COL.name, grow: true, align: 'left', first: true, fill: 60 },
  { width: COL.status, fill: 65, height: 24, radius: 'full' },
  { width: COL.proxy, fill: 70 },
  { width: COL.url, grow: true, align: 'left', fill: 70 },
  { width: COL.checked, fill: 75 },
  { width: COL.actions, sticky: 'right', fill: 60, height: 16 },
];

const proxyLabel = (proxy) =>
  proxy ? `${proxy.host}${proxy.location ? ` · ${proxy.location}` : ''}` : '—';

const ActionButtons = ({ decoyApp, checking, onCheck, onEdit, onDelete, mobile }) => {
  const size = mobile ? 'w-8 h-8 max-md:w-11 max-md:h-11' : 'w-7 h-7';
  return (
    <div className="flex items-center gap-1">
      <button
        onClick={() => onCheck(decoyApp)}
        disabled={checking}
        aria-label="Check now"
        title="Check now"
        className={`${size} inline-flex items-center justify-center rounded-md text-ink-muted hover:text-brand-500 hover:bg-surface transition-colors disabled:text-ink-disabled`}
      >
        <IconRefresh size={16} className={checking ? 'animate-spin' : ''} />
      </button>
      <button
        onClick={() => onEdit(decoyApp)}
        aria-label="Edit decoy app"
        className={`${size} inline-flex items-center justify-center rounded-md text-ink-muted hover:text-brand-500 hover:bg-surface transition-colors`}
      >
        <IconEdit size={16} />
      </button>
      <button
        onClick={() => onDelete(decoyApp)}
        aria-label="Delete decoy app"
        className={`${size} inline-flex items-center justify-center rounded-md text-ink-muted hover:text-danger hover:bg-surface transition-colors`}
      >
        <IconTrash size={16} />
      </button>
    </div>
  );
};

const AppTitle = ({ decoyApp, large }) => (
  <div className="flex items-center gap-2.5 min-w-0">
    {decoyApp.iconUrl ? (
      <img
        src={decoyApp.iconUrl}
        alt=""
        className={`${large ? 'w-10 h-10' : 'w-9 h-9'} rounded-lg object-cover border border-line shrink-0`}
      />
    ) : (
      <span className={`${large ? 'w-10 h-10' : 'w-9 h-9'} rounded-lg bg-surface border border-line shrink-0`} />
    )}
    <div className="min-w-0">
      <p className={`${large ? 'text-[15px]' : 'text-[13px]'} font-medium text-ink truncate`}>{decoyApp.name}</p>
      <p className="text-[11px] text-ink-muted truncate">{STORE_TYPE_LABELS[decoyApp.storeType] || ''}</p>
    </div>
  </div>
);

const DecoyAppsPanel = () => {
  const [decoyApps, setDecoyApps] = useState([]);
  const [rotationProxies, setRotationProxies] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [checkingIds, setCheckingIds] = useState([]);
  const [search, setSearch] = useState('');

  const fetchDecoyApps = async ({ silent = false } = {}) => {
    try {
      if (!silent) setLoading(true);
      const response = await axios.get('/api/decoy-apps');
      if (response.data.success) {
        setDecoyApps(response.data.data.decoyApps);
        setRotationProxies(response.data.data.rotationProxies);
      }
    } catch (err) {
      setError('Failed to fetch decoy apps');
      console.error(err);
    } finally {
      if (!silent) setLoading(false);
    }
  };

  useEffect(() => {
    fetchDecoyApps();
    // The queue checks one app every 1–5 min — refresh quietly to show progress
    const interval = setInterval(() => fetchDecoyApps({ silent: true }), 60000);
    return () => clearInterval(interval);
  }, []);

  const counts = useMemo(() => ({
    all: decoyApps.length,
    available: decoyApps.filter((a) => a.lastCheckStatus === 'available').length,
    unavailable: decoyApps.filter((a) => a.lastCheckStatus === 'unavailable').length,
  }), [decoyApps]);

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return decoyApps;
    return decoyApps.filter((a) => [a.name, a.storeUrl].some((v) => v && v.toLowerCase().includes(q)));
  }, [decoyApps, search]);

  const handleCheck = async (decoyApp) => {
    setCheckingIds((ids) => [...ids, decoyApp._id]);
    try {
      await axios.post(`/api/decoy-apps/${decoyApp._id}/check`);
      await fetchDecoyApps({ silent: true });
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to check decoy app');
    } finally {
      setCheckingIds((ids) => ids.filter((id) => id !== decoyApp._id));
    }
  };

  const handleEdit = (decoyApp) => {
    setEditing(decoyApp);
    setShowModal(true);
  };

  const handleAdd = () => {
    setEditing(null);
    setShowModal(true);
  };

  const confirmDelete = async () => {
    const target = deleteTarget;
    setDeleteTarget(null);
    if (!target) return;
    try {
      await axios.delete(`/api/decoy-apps/${target._id}`);
      fetchDecoyApps({ silent: true });
    } catch (err) {
      setError('Failed to delete decoy app');
      console.error(err);
    }
  };

  const handleModalClose = (shouldRefresh) => {
    setShowModal(false);
    setEditing(null);
    if (shouldRefresh) fetchDecoyApps({ silent: true });
  };

  const addButton = (
    <PrimaryButton onClick={handleAdd}>
      <IconPlus size={14} />
      Add apps
    </PrimaryButton>
  );

  const tableHeader = (
    <div className="flex items-stretch h-10 bg-surface border-b border-line">
      <HeaderCell width={COL.name} grow align="left" first>App</HeaderCell>
      <HeaderCell width={COL.status}>Status</HeaderCell>
      <HeaderCell width={COL.proxy}>Last proxy</HeaderCell>
      <HeaderCell width={COL.url} grow align="left">Store URL</HeaderCell>
      <HeaderCell width={COL.checked}>Last checked</HeaderCell>
      <HeaderCell width={COL.actions} sticky="right" />
    </div>
  );

  const actionProps = (decoyApp) => ({
    decoyApp,
    checking: checkingIds.includes(decoyApp._id),
    onCheck: handleCheck,
    onEdit: handleEdit,
    onDelete: setDeleteTarget,
  });

  return (
    <>
      <LoadingAnnouncer loading={loading} label="Loading decoy apps" loadedLabel="Decoy apps loaded" />

      <ErrorAlert message={error} onDismiss={() => setError('')} />

      {loading ? (
        <>
          <div className="flex flex-wrap gap-3">
            {['Total', 'Available', 'Unavailable', 'Proxies in rotation'].map((label) => (
              <InfoCard key={label} label={label} loading />
            ))}
          </div>
          <Card className="flex flex-col">
            <SkeletonListToolbar />
            <div className="hidden md:block overflow-x-auto">
              <div className="min-w-[1140px]">
                {tableHeader}
                <SkeletonTableRows columns={SKELETON_COLUMNS} rows={6} rowClassName={ROW} />
              </div>
            </div>
            <div className="md:hidden divide-y divide-line">
              {Array.from({ length: 3 }, (_, index) => <SkeletonListCard key={index} withIcon />)}
            </div>
          </Card>
        </>
      ) : decoyApps.length === 0 ? (
        <Card>
          <EmptyState
            title="No decoy apps yet"
            description="Add apps to be checked through the proxies of the monitored apps."
            action={addButton}
          />
        </Card>
      ) : (
        <>
          <div className="flex flex-wrap gap-3">
            <InfoCard label="Total" value={counts.all} />
            <InfoCard label="Available" value={counts.available} accent="text-success" />
            <InfoCard label="Unavailable" value={counts.unavailable} accent="text-danger" />
            <InfoCard
              label="Proxies in rotation"
              value={rotationProxies}
              accent={rotationProxies === 0 ? 'text-danger' : undefined}
              hint={rotationProxies === 0 ? 'No proxies assigned to apps' : undefined}
            />
          </div>

          <Card className="flex flex-col">
            <div className="shrink-0 flex flex-wrap items-center gap-3 p-4 border-b border-line">
              <SearchInput
                value={search}
                onChange={setSearch}
                placeholder="Search by name, store URL…"
                width={230}
              />
              <p className="text-xs text-ink-muted flex-1 min-w-[200px]">
                One app every 1–5 min, through a random proxy of the monitored apps
              </p>
              {addButton}
            </div>

            {/* Desktop table */}
            <div className="hidden md:block overflow-x-auto">
              <div className="min-w-[1140px]">
                {tableHeader}
                {visible.length === 0 ? (
                  <div className="py-12 text-center text-sm text-ink-muted">No apps found.</div>
                ) : (
                  visible.map((decoyApp) => (
                    <div key={decoyApp._id} className={ROW}>
                      <BodyCell width={COL.name} grow align="left" first>
                        <AppTitle decoyApp={decoyApp} />
                      </BodyCell>
                      <BodyCell width={COL.status}>
                        <span title={decoyApp.lastErrorMessage || ''}>
                          <StatusBadge status={decoyApp.lastCheckStatus} />
                        </span>
                      </BodyCell>
                      <BodyCell width={COL.proxy}>
                        <span className="text-ink-muted whitespace-nowrap truncate">
                          {proxyLabel(decoyApp.lastProxy)}
                        </span>
                      </BodyCell>
                      <BodyCell width={COL.url} grow align="left">
                        <a
                          href={decoyApp.storeUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-brand-500 hover:underline truncate"
                          title={decoyApp.storeUrl}
                        >
                          {decoyApp.storeUrl}
                        </a>
                      </BodyCell>
                      <BodyCell width={COL.checked}>
                        <span className="text-ink-muted whitespace-nowrap">
                          {formatDate(decoyApp.lastCheckedAt)}
                        </span>
                      </BodyCell>
                      <BodyCell width={COL.actions} sticky="right">
                        <ActionButtons {...actionProps(decoyApp)} />
                      </BodyCell>
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* Mobile card list */}
            <div className="md:hidden divide-y divide-line">
              {visible.length === 0 ? (
                <div className="py-12 text-center text-sm text-ink-muted">No apps found.</div>
              ) : (
                visible.map((decoyApp) => (
                  <div key={decoyApp._id} className="p-4">
                    <div className="flex justify-between items-start gap-3 mb-3">
                      <AppTitle decoyApp={decoyApp} large />
                      <StatusBadge status={decoyApp.lastCheckStatus} />
                    </div>
                    <div className="flex flex-col gap-2 text-xs">
                      <div>
                        <span className="text-ink-muted">Store URL</span>
                        <a
                          href={decoyApp.storeUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="block text-brand-500 break-all hover:underline"
                        >
                          {decoyApp.storeUrl}
                        </a>
                      </div>
                      <div>
                        <span className="text-ink-muted">Last proxy</span>
                        <p className="text-ink">{proxyLabel(decoyApp.lastProxy)}</p>
                      </div>
                      <div>
                        <span className="text-ink-muted">Last checked</span>
                        <p className="text-ink">{formatDate(decoyApp.lastCheckedAt)}</p>
                      </div>
                    </div>
                    <div className="flex justify-end pt-3 mt-3 border-t border-line">
                      <ActionButtons {...actionProps(decoyApp)} mobile />
                    </div>
                  </div>
                ))
              )}
            </div>
          </Card>
        </>
      )}

      {showModal && <DecoyAppModal decoyApp={editing} onClose={handleModalClose} />}

      <DeleteModal
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={confirmDelete}
        itemType="decoy app"
        itemName={deleteTarget?.name}
      />
    </>
  );
};

export default DecoyAppsPanel;
